import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) {
    return <Navigate to="/admin/quotes" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin/quotes", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" ")
          : "Login failed. Please check your credentials.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full bg-transparent border border-black/70 focus:border-black focus:ring-0 outline-none px-4 py-3 font-mono text-[13px] tracking-wide placeholder:text-black/40 text-black transition-colors";
  const label =
    "font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-2 block";

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-black flex items-center justify-center px-6 py-16 paper-grain">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <img src={LOGO.mark} alt="Arvind Printers" className="h-10 w-auto" />
          <div>
            <div className="font-display text-lg leading-none">Arvind Printers</div>
            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-black/60 mt-1">
              Admin Console
            </div>
          </div>
        </div>

        <div className="crop-marks relative border border-black p-8 md:p-10">
          <span className="cm-tr" />
          <span className="cm-br" />

          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-3">
            <Lock className="w-3.5 h-3.5" /> Restricted Area
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            Sign in <br />
            <em className="italic">to the press.</em>
          </h1>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className={label}>Email</label>
              <input
                className={field}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@arvindprinters.com"
                required
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <label className={label}>Password</label>
              <input
                className={field}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="admin-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-offset w-full justify-center disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Signing in
                </>
              ) : (
                <>
                  Enter Console <ArrowUpRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-black/50 text-center">
          Back to <a href="/" className="underline hover:text-black">Arvindprinters.com</a>
        </div>
      </div>
    </div>
  );
}
